import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { settingsApi, projectsApi, authApi } from "@/lib/crmApi";

const SettingsContext = createContext({
  companyName: "SOGI SARL",
  activeProjectId: null,
  activeProject: null,
  projects: [],
  loading: true,
  refresh: () => {},
  setCompanyName: () => {},
  setActiveProject: () => {},
});

export const SettingsProvider = ({ children }) => {
  const [companyName, setCompany] = useState("SOGI SARL");
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, p, me] = await Promise.all([
        settingsApi.get(),
        projectsApi.list(),
        // Pull /me so we can detect the stale-cookie state below. Failures
        // are non-fatal: if /me is unreachable we just skip the sync.
        authApi.me().catch(() => null),
      ]);
      setCompany(s.company_name || "SOGI SARL");
      setActiveProjectId(s.active_project_id || null);
      setActiveProject(s.active_project || null);
      // Always coerce to a real array — `p || []` lets non-array truthy values
      // through (e.g. a stringified error response), which then crashes any
      // .map() consumer downstream.
      setProjects(Array.isArray(p) ? p : []);

      // Cookie/active-project sync on initial load.
      //
      // The topbar ProjectSelector calls `setActiveProject(id)` which both
      // updates the global active_project_id AND posts the cookie. But on
      // a fresh login or hard refresh, only `settings.active_project_id`
      // is set — the cookie was never written, so the inbox header still
      // says "Tous les projets" and the WhatsApp scoping falls back to
      // cross-project. We auto-heal here by writing the cookie when:
      //   - an active project IS set globally
      //   - the user's `current_projet` cookie is empty
      //   - the active project is in the user's allowed list (or admin)
      // It's a one-time silent sync — the user sees the correct scope
      // immediately without having to manually click the picker.
      if (
        me &&
        s.active_project_id &&
        !me.current_projet &&
        (
          me.is_admin ||
          (me.allowed_projets || []).includes(s.active_project_id)
        )
      ) {
        try {
          await authApi.selectProgramme(s.active_project_id);
          window.dispatchEvent(new CustomEvent("crm:auth-refresh"));
        } catch (err) {
          console.warn("[settings] cookie sync failed", err);
        }
      }

      // v1.8.1 — strict isolation for commercials. If the current user is
      // NOT an admin AND has no active project (cookie empty and global
      // default not set or unavailable), force them into the first project
      // of their `allowed_projets`. We never want a commercial to land on
      // "Tous les projets" — that view doesn't even render in their UI but
      // a stale cookie could still scope queries cross-project on the API.
      if (me && !me.is_admin) {
        const allowed = Array.isArray(me.allowed_projets) ? me.allowed_projets : [];
        const allCommercialProjects = Array.isArray(p) ? p : [];
        const valid = allCommercialProjects.find((pp) => allowed.includes(pp.id));
        const needsPin =
          !s.active_project_id ||
          !allowed.includes(s.active_project_id);
        if (needsPin && valid) {
          try {
            const upd = await settingsApi.update({ active_project_id: valid.id });
            setActiveProjectId(upd.active_project_id || null);
            setActiveProject(upd.active_project || null);
            await authApi.selectProgramme(valid.id);
            window.dispatchEvent(new CustomEvent("crm:auth-refresh"));
          } catch (err) {
            console.warn("[settings] commercial auto-pin failed", err);
          }
        }
      }
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setCompanyName = async (name) => {
    const s = await settingsApi.update({ company_name: name });
    setCompany(s.company_name);
    return s;
  };

  const setActiveProjectHandler = async (projectId) => {
    // Two-step switch:
    //   1. `settings.active_project_id` — global default project of the
    //      installation (e.g. used by /seed, defaults on lead creation).
    //   2. `current_projet` cookie — per-user "what am I currently viewing"
    //      pointer. This is what /api/whatsapp/inbox + /api/whatsapp/messages
    //      read to scope conversations to a project's WhatsApp number.
    //
    // Before this fix, clicking the topbar ProjectSelector only updated #1.
    // Result: an admin who picked "RYAD Mermoz" still saw "RYAD Almadies"
    // conversations in the inbox because the cookie remained empty and the
    // backend fell back to the cross-project view.
    const s = await settingsApi.update({ active_project_id: projectId || null });
    setActiveProjectId(s.active_project_id || null);
    setActiveProject(s.active_project || null);
    try {
      // selectProgramme accepts both a UUID and a name. We pass the ID so the
      // backend can also write the legacy `current_programme` cookie (it
      // resolves UUID → nom server-side). An empty string clears both cookies
      // — that's the "Tous les projets" case.
      await authApi.selectProgramme(projectId || "");
      // Notify AuthContext to re-fetch /me so components reading
      // `user.current_projet` (inbox session indicator, WhatsApp scoping)
      // pick up the change without a full page reload.
      window.dispatchEvent(new CustomEvent("crm:auth-refresh"));
    } catch (e) {
      // Cookie post-set failure shouldn't block the UI switch — the user
      // will still see the right scoped data once the cookie eventually
      // gets posted (e.g. on next refresh). Log only.
      console.warn("[settings] selectProgramme failed", e);
    }
    return s;
  };

  return (
    <SettingsContext.Provider
      value={{
        companyName,
        activeProjectId,
        activeProject,
        projects,
        loading,
        refresh: load,
        setCompanyName,
        setActiveProject: setActiveProjectHandler,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
